"use client";

import { type ReactNode, useEffect, useRef } from "react";
import { PRODUCT_SUMMARY_SCROLL_ATTRIBUTE } from "./product-hero-scroll-pin.constants";

const SITE_HEADER_SELECTOR = "[data-site-header]";

/**
 * Pins the product hero while page scroll advances the summary, keeping the
 * site's fixed header in place when present.
 *
 * Inactive whenever the panel does not overflow (mobile layout, short content).
 */
export interface ProductHeroScrollPinProps {
  children: ReactNode;
  className?: string;
}

export function ProductHeroScrollPin(
  props: Readonly<ProductHeroScrollPinProps>
) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const pinned = pinnedRef.current;

    if (!(section && pinned)) {
      return;
    }

    const panel = pinned.querySelector<HTMLElement>(
      `[${PRODUCT_SUMMARY_SCROLL_ATTRIBUTE}]`
    );

    if (!panel) {
      return;
    }

    const header = document.querySelector<HTMLElement>(SITE_HEADER_SELECTOR);
    const hasPersistentHeader = header?.dataset.siteHeader === "persistent";
    const mainEl = section.closest<HTMLElement>("main");

    let pinnedDistance = 0;
    let stickyOffset = 0;
    let frameId: number | null = null;

    const clearPin = () => {
      section.style.removeProperty("height");
      pinned.style.removeProperty("position");
      pinned.style.removeProperty("top");
      panel.style.removeProperty("overflow-y");
      panel.scrollTop = 0;
      if (!hasPersistentHeader) {
        header?.style.removeProperty("position");
        header?.style.removeProperty("top");
        header?.style.removeProperty("z-index");
      }
    };

    /**
     * Maps how far the section has been scrolled past the pin's starting point
     * onto the panel's own scroll offset, 1:1 — the panel advances at exactly
     * the speed the page would have scrolled.
     */
    const sync = () => {
      frameId = null;

      if (pinnedDistance <= 0) {
        return;
      }

      const scrolledPast = stickyOffset - section.getBoundingClientRect().top;

      panel.scrollTop = Math.min(Math.max(scrolledPast, 0), pinnedDistance);

      // The header holds its place for the length of the lock, then scrolls off
      // at the page's own rate — pushing it up by however far we are past the
      // end of the lock leaves it exactly where unpinned flow would have put it.
      if (header && !hasPersistentHeader) {
        const released = Math.max(0, scrolledPast - pinnedDistance);

        header.style.top = `${-released}px`;
      }
    };

    const requestSync = () => {
      frameId ??= window.requestAnimationFrame(sync);
    };

    const measure = () => {
      // Measure against the panel's own layout, which is only height-capped
      // from the `lg` breakpoint up; below that it never overflows.
      pinnedDistance = Math.max(0, panel.scrollHeight - panel.clientHeight);

      if (pinnedDistance <= 0) {
        clearPin();
        return;
      }

      // Holding the header in place lets the hero pin directly beneath it from
      // the very first pixel of scroll. Pinning at the top instead would mean
      // the page still scrolls the header's height before the lock engages.
      const mainTopPadding = mainEl
        ? Number.parseFloat(window.getComputedStyle(mainEl).paddingTop)
        : 0;
      const headerHeight = header?.getBoundingClientRect().height ?? 0;
      // The fixed header's height is already included in the page's top inset.
      stickyOffset = hasPersistentHeader
        ? Math.max(headerHeight, mainTopPadding)
        : headerHeight + mainTopPadding;

      if (header && !hasPersistentHeader) {
        header.style.position = "sticky";
        header.style.top = "0px";
        header.style.zIndex = "10";
      }

      panel.style.overflowY = "hidden";
      pinned.style.position = "sticky";
      pinned.style.top = `${stickyOffset}px`;

      // Reserve everything below the header so whatever follows the hero cannot
      // creep into view while the panel is still scrolling.
      const pinnedViewport = Math.max(
        pinned.offsetHeight,
        window.innerHeight - stickyOffset
      );

      section.style.height = `${pinnedViewport + pinnedDistance}px`;

      sync();
    };

    measure();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);

    // The panel is height-capped, so its own box never resizes — a
    // ResizeObserver on the panel alone never reports the growth that actually
    // matters: images loading and web-font swaps make the panel's children
    // taller (so scrollHeight, and thus pinnedDistance, grows) without changing
    // the panel box or the child count. Observing the children directly catches
    // that so the panel's bottom content stays reachable instead of being
    // clipped until the next resize.
    const observeTargets = () => {
      if (!resizeObserver) {
        return;
      }
      resizeObserver.disconnect();
      resizeObserver.observe(panel);
      if (header) {
        resizeObserver.observe(header);
      }
      for (const child of Array.from(panel.children)) {
        resizeObserver.observe(child);
      }
    };

    // Dynamic content (e.g. Notification, Smartfit widget) mounts as new
    // children after load. Only a change to the panel's own child list needs a
    // re-subscribe (a new child to observe); mutations nested inside an existing
    // child just need a re-measure, so avoid resetting every ResizeObserver
    // subscription on each nested change.
    const mutationObserver =
      typeof MutationObserver === "undefined"
        ? null
        : new MutationObserver((mutations) => {
            if (mutations.some((mutation) => mutation.target === panel)) {
              observeTargets();
            }
            measure();
          });

    observeTargets();
    mutationObserver?.observe(panel, { childList: true, subtree: true });
    window.addEventListener("scroll", requestSync, { passive: true });
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener("scroll", requestSync);
      window.removeEventListener("resize", measure);

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      clearPin();
    };
  }, []);

  return (
    <div data-testid="product-hero-scroll-section" ref={sectionRef}>
      <div className={props.className} ref={pinnedRef}>
        {props.children}
      </div>
    </div>
  );
}
